import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getVandalizedPosters,
    updatePosterVandalism,
    type PosterAdminFilter,
    type UpdatePosterVandalismRequest,
    type VandalizedPosterRow
} from "../api/posters";
import { useCampaigns } from "../api/campaigns";
import { mapboxAccessToken, mapStyle, mapInitialView } from "../map/config";
import { showError, showSuccess } from "../shared/toast";
import { PosterAdminFilters, PosterAdminTabs, defaultPosterFilter } from "./components/PosterAdminNav";
import {
    HANDLING_LABELS,
    dateText,
    isClosed,
    matchesHandling,
    type HandlingFilter
} from "./posterVandalismFilter";
import "./poster-admin.css";
import "./poster-vandalism-map.css";

const SOURCE_ID = "poster-vandalism";
const CIRCLES_LAYER_ID = "poster-vandalism-circles";
const LABELS_LAYER_ID = "poster-vandalism-labels";

/** Ej polisanmäld och fortfarande öppen – det som ska handläggas härnäst. */
const COLOR_NOT_REPORTED = "#D92626";
/** Polisanmäld men ärendet är inte stängt. */
const COLOR_REPORTED = "#E8A317";
/** Nedtagen eller lagad. */
const COLOR_CLOSED = "#6B9E87";

type MarkerState = "notReported" | "reported" | "closed";

export const markerState = (row: VandalizedPosterRow): MarkerState => {
    if (isClosed(row)) return "closed";
    return row.policeReportedAt === null ? "notReported" : "reported";
};

const MARKER_COLORS: Record<MarkerState, string> = {
    notReported: COLOR_NOT_REPORTED,
    reported: COLOR_REPORTED,
    closed: COLOR_CLOSED
};

const MARKER_LABELS: Record<MarkerState, string> = {
    notReported: "Ej polisanmäld",
    reported: "Polisanmäld",
    closed: "Åtgärdad"
};

/** Affischer utan giltig position kan inte ritas ut – de räknas men listas separat. */
export const hasCoordinates = (row: VandalizedPosterRow): boolean =>
    Number.isFinite(row.latitude) &&
    Number.isFinite(row.longitude) &&
    !(row.latitude === 0 && row.longitude === 0);

export const PosterVandalismMapPage = (): JSX.Element => {
    const queryClient = useQueryClient();
    const [filter, setFilter] = useState<PosterAdminFilter>(defaultPosterFilter);
    const [handling, setHandling] = useState<HandlingFilter>("notReported");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [reportNumberDraft, setReportNumberDraft] = useState<string | null>(null);
    const [noteDraft, setNoteDraft] = useState<string | null>(null);

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const hoverPopupRef = useRef<mapboxgl.Popup | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    /** Kartan zoomas om bara när urvalet byter innehåll, inte vid varje sparning. */
    const lastFitKeyRef = useRef<string | null>(null);

    const { data: campaigns = [] } = useCampaigns();
    const { data: rows = [], isLoading } = useQuery({
        queryKey: ["posters", "admin", "vandalized", filter],
        queryFn: () => getVandalizedPosters(filter),
        staleTime: 30_000
    });

    const mutation = useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdatePosterVandalismRequest }) =>
            updatePosterVandalism(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["posters", "admin"] });
            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
            showSuccess("Vandaliseringsärendet uppdaterat");
        },
        onError: () => showError("Kunde inte spara ändringen")
    });

    const save = (id: string, body: UpdatePosterVandalismRequest) => mutation.mutate({ id, body });

    const visibleRows = useMemo(
        () => rows.filter((row) => matchesHandling(row, handling)),
        [rows, handling]
    );

    const mappableRows = useMemo(() => visibleRows.filter(hasCoordinates), [visibleRows]);
    const missingCoordinates = visibleRows.length - mappableRows.length;

    const counts = useMemo(
        () => ({
            total: visibleRows.length,
            quantity: visibleRows.reduce((sum, row) => sum + row.quantity, 0),
            notReported: visibleRows.filter(
                (row) => !isClosed(row) && row.policeReportedAt === null
            ).length,
            reported: visibleRows.filter((row) => !isClosed(row) && row.policeReportedAt !== null)
                .length,
            closed: visibleRows.filter(isClosed).length
        }),
        [visibleRows]
    );

    const selected = useMemo(
        () => mappableRows.find((row) => row.id === selectedId) ?? null,
        [mappableRows, selectedId]
    );

    // Ett ärende som filtreras bort (t.ex. när det just polisanmälts i vyn "Ej
    // polisanmälda") ska inte ligga kvar markerat i sidopanelen.
    useEffect(() => {
        if (selectedId && !mappableRows.some((row) => row.id === selectedId)) {
            setSelectedId(null);
        }
    }, [mappableRows, selectedId]);

    useEffect(() => {
        setReportNumberDraft(null);
        setNoteDraft(null);
    }, [selectedId]);

    // Kartcontainern renderas alltid, så init-effekten hittar alltid en DOM-nod.
    useEffect(() => {
        if (mapRef.current || !mapContainerRef.current || !mapboxAccessToken) {
            return;
        }

        mapboxgl.accessToken = mapboxAccessToken;
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapStyle,
            center: mapInitialView.center,
            zoom: 11,
            minZoom: 3,
            maxZoom: 19
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");

        const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 14 });
        hoverPopupRef.current = popup;

        map.once("load", () => {
            map.addSource(SOURCE_ID, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] }
            });

            map.addLayer({
                id: CIRCLES_LAYER_ID,
                type: "circle",
                source: SOURCE_ID,
                paint: {
                    "circle-radius": [
                        "+",
                        ["case", ["get", "selected"], 4, 0],
                        ["interpolate", ["linear"], ["get", "quantity"], 1, 8, 10, 16]
                    ],
                    "circle-color": [
                        "match",
                        ["get", "state"],
                        "notReported",
                        COLOR_NOT_REPORTED,
                        "reported",
                        COLOR_REPORTED,
                        COLOR_CLOSED
                    ],
                    "circle-opacity": 0.85,
                    "circle-stroke-color": ["case", ["get", "selected"], "#111827", "#ffffff"],
                    "circle-stroke-width": ["case", ["get", "selected"], 3, 2]
                }
            });

            map.addLayer({
                id: LABELS_LAYER_ID,
                type: "symbol",
                source: SOURCE_ID,
                layout: {
                    "text-field": ["to-string", ["get", "quantity"]],
                    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                    "text-size": 11,
                    "text-allow-overlap": true
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "rgba(17, 24, 39, 0.55)",
                    "text-halo-width": 1
                }
            });

            map.on("click", CIRCLES_LAYER_ID, (e) => {
                const id = e.features?.[0]?.properties?.id;
                if (typeof id === "string") {
                    setSelectedId(id);
                }
            });

            map.on("mouseenter", CIRCLES_LAYER_ID, (e) => {
                map.getCanvas().style.cursor = "pointer";
                const feature = e.features?.[0];
                if (!feature) return;
                const props = feature.properties ?? {};
                const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
                popup
                    .setLngLat([lng, lat])
                    .setText(`${props.address ?? "Adress saknas"} — ${props.quantity ?? "?"} st · ${props.stateLabel ?? ""}`)
                    .addTo(map);
            });

            map.on("mouseleave", CIRCLES_LAYER_ID, () => {
                map.getCanvas().style.cursor = "";
                popup.remove();
            });

            setIsMapReady(true);
        });

        mapRef.current = map;

        return () => {
            hoverPopupRef.current?.remove();
            hoverPopupRef.current = null;
            map.remove();
            mapRef.current = null;
            setIsMapReady(false);
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) {
            return;
        }

        const features: GeoJSON.Feature[] = mappableRows.map((row) => {
            const state = markerState(row);
            return {
                type: "Feature",
                geometry: { type: "Point", coordinates: [row.longitude, row.latitude] },
                properties: {
                    id: row.id,
                    quantity: row.quantity,
                    state,
                    stateLabel: MARKER_LABELS[state],
                    address: row.addressLabel ?? "Adress saknas",
                    selected: row.id === selectedId
                }
            };
        });

        (map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features
        });

        const fitKey = mappableRows
            .map((row) => row.id)
            .sort()
            .join(",");
        if (mappableRows.length > 0 && fitKey !== lastFitKeyRef.current) {
            lastFitKeyRef.current = fitKey;
            const bounds = new mapboxgl.LngLatBounds();
            for (const row of mappableRows) {
                bounds.extend([row.longitude, row.latitude]);
            }
            map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 400 });
        }
    }, [mappableRows, selectedId, isMapReady]);

    const focusRow = (row: VandalizedPosterRow) => {
        setSelectedId(row.id);
        const map = mapRef.current;
        if (!map) return;
        map.easeTo({
            center: [row.longitude, row.latitude],
            zoom: Math.max(map.getZoom(), 16),
            duration: 500
        });
    };

    const zoomToAll = () => {
        const map = mapRef.current;
        if (!map || mappableRows.length === 0) return;
        const bounds = new mapboxgl.LngLatBounds();
        for (const row of mappableRows) {
            bounds.extend([row.longitude, row.latitude]);
        }
        map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 400 });
    };

    return (
        <section className="poster-admin" aria-label="Karta över vandaliserade affischer">
            <div className="poster-admin__header">
                <h2>Affischering</h2>
                <button
                    type="button"
                    className="poster-admin__button"
                    onClick={zoomToAll}
                    disabled={mappableRows.length === 0}
                >
                    Zooma till alla
                </button>
            </div>

            <PosterAdminTabs />

            <PosterAdminFilters
                filter={filter}
                onChange={setFilter}
                campaigns={campaigns}
                extra={
                    <div className="filter-group">
                        <label htmlFor="poster-map-handling">Visa:</label>
                        <select
                            id="poster-map-handling"
                            className="filter-select"
                            value={handling}
                            onChange={(e) => setHandling(e.target.value as HandlingFilter)}
                        >
                            {(Object.keys(HANDLING_LABELS) as HandlingFilter[]).map((key) => (
                                <option key={key} value={key}>
                                    {HANDLING_LABELS[key]}
                                </option>
                            ))}
                        </select>
                    </div>
                }
            />

            <div className="poster-admin__summary">
                <div className="summary-card">
                    <p className="summary-card__label">I urvalet</p>
                    <p className="summary-card__value">{counts.total}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Affischer</p>
                    <p className="summary-card__value">{counts.quantity}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Ej polisanmälda</p>
                    <p className="summary-card__value">{counts.notReported}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Polisanmälda</p>
                    <p className="summary-card__value">{counts.reported}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Åtgärdade</p>
                    <p className="summary-card__value">{counts.closed}</p>
                </div>
            </div>

            <div className="poster-map__legend">
                <span className="poster-map__legend-item">
                    <i style={{ background: COLOR_NOT_REPORTED }} />
                    Ej polisanmäld
                </span>
                <span className="poster-map__legend-item">
                    <i style={{ background: COLOR_REPORTED }} />
                    Polisanmäld
                </span>
                <span className="poster-map__legend-item">
                    <i style={{ background: COLOR_CLOSED }} />
                    Nedtagen / lagad
                </span>
                <span className="poster-map__legend-hint">
                    Cirkelns storlek och siffra visar antalet affischer på platsen.
                    {missingCoordinates > 0 &&
                        ` ${missingCoordinates} ärende(n) saknar koordinater och visas inte i kartan.`}
                </span>
            </div>

            {!mapboxAccessToken && (
                <p className="poster-admin__empty">
                    Kartan kan inte visas – Mapbox-nyckel saknas (VITE_MAPBOX_TOKEN).
                </p>
            )}

            <div className="poster-map__layout">
                <div className="poster-map__canvas" ref={mapContainerRef} />

                <aside className="poster-map__panel" aria-label="Valt vandaliseringsärende">
                    {isLoading && <p className="poster-admin__empty">Hämtar vandaliseringar…</p>}

                    {!isLoading && mappableRows.length === 0 && (
                        <p className="poster-admin__empty">Inga vandaliserade affischer i urvalet.</p>
                    )}

                    {!isLoading && mappableRows.length > 0 && !selected && (
                        <>
                            <p className="poster-admin__hint">
                                Klicka på en punkt i kartan – eller i listan nedan – för att
                                handlägga ärendet.
                            </p>
                            <ul className="poster-map__list">
                                {mappableRows.map((row) => (
                                    <li key={row.id}>
                                        <button
                                            type="button"
                                            className="poster-map__list-item"
                                            onClick={() => focusRow(row)}
                                        >
                                            <i style={{ background: MARKER_COLORS[markerState(row)] }} />
                                            <span>
                                                <span className="poster-admin__address">
                                                    {row.addressLabel ?? "Adress saknas"}
                                                </span>
                                                <span className="poster-admin__muted">
                                                    {row.quantity} st · vandaliserad{" "}
                                                    {dateText(row.vandalizedAt)}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {selected && (
                        <div className="poster-map__detail">
                            <div className="poster-map__detail-head">
                                <h3 className="poster-admin__section-title">
                                    {selected.addressLabel ?? "Adress saknas"}
                                </h3>
                                <button
                                    type="button"
                                    className="filter-clear-button"
                                    onClick={() => setSelectedId(null)}
                                >
                                    Tillbaka till listan
                                </button>
                            </div>

                            <p className="poster-admin__muted">
                                {selected.campaignName} · {selected.quantity} st affischer
                            </p>
                            <a
                                className="poster-admin__coords"
                                href={`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {selected.latitude.toFixed(5)}, {selected.longitude.toFixed(5)}
                            </a>

                            <dl className="poster-map__facts">
                                <div>
                                    <dt>Uppsatt</dt>
                                    <dd>
                                        {dateText(selected.placedAt)}
                                        <span className="poster-admin__muted">
                                            {" "}
                                            av {selected.placedByName}
                                        </span>
                                    </dd>
                                </div>
                                <div>
                                    <dt>Vandaliserad</dt>
                                    <dd>
                                        {dateText(selected.vandalizedAt)}
                                        {selected.vandalizedByName && (
                                            <span className="poster-admin__muted">
                                                {" "}
                                                av {selected.vandalizedByName}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            </dl>

                            <label className="poster-admin__check">
                                <input
                                    type="checkbox"
                                    checked={selected.policeReportedAt !== null}
                                    disabled={mutation.isPending}
                                    onChange={(e) =>
                                        save(selected.id, { policeReported: e.target.checked })
                                    }
                                />
                                Polisanmäld
                                {selected.policeReportedAt && (
                                    <span className="poster-admin__muted">
                                        {dateText(selected.policeReportedAt)}
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                className="poster-admin__input"
                                placeholder="Diarienummer"
                                value={reportNumberDraft ?? selected.policeReportNumber ?? ""}
                                onChange={(e) => setReportNumberDraft(e.target.value)}
                                onBlur={(e) => {
                                    const value = e.target.value.trim();
                                    if (value === (selected.policeReportNumber ?? "")) return;
                                    save(selected.id, { policeReportNumber: value });
                                }}
                            />

                            <label className="poster-admin__check">
                                <input
                                    type="checkbox"
                                    checked={selected.takenDownAt !== null}
                                    disabled={mutation.isPending}
                                    onChange={(e) =>
                                        save(selected.id, { takenDown: e.target.checked })
                                    }
                                />
                                Nedtagen
                                {selected.takenDownAt && (
                                    <span className="poster-admin__muted">
                                        {dateText(selected.takenDownAt)}
                                    </span>
                                )}
                            </label>

                            <label className="poster-admin__check">
                                <input
                                    type="checkbox"
                                    checked={selected.repairedAt !== null}
                                    disabled={mutation.isPending}
                                    onChange={(e) => save(selected.id, { repaired: e.target.checked })}
                                />
                                Lagad
                                {selected.repairedAt && (
                                    <span className="poster-admin__muted">
                                        {dateText(selected.repairedAt)}
                                    </span>
                                )}
                            </label>

                            <textarea
                                className="poster-admin__notes"
                                rows={3}
                                placeholder="Anteckning för polisanmälan…"
                                value={noteDraft ?? selected.vandalismNotes ?? ""}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                onBlur={(e) => {
                                    const value = e.target.value.trim();
                                    if (value === (selected.vandalismNotes ?? "")) return;
                                    save(selected.id, { vandalismNotes: value });
                                }}
                            />
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
};
